import { CdkPortalOutlet } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  Input,
  OnChanges,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  viewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatStep, MatStepLabel, MatStepper, MatStepperIcon } from '@angular/material/stepper';
import { ButtonComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import { GroupValueType, MultiStepFormItemModel, StepItemModel } from '@libs/form/models';
import { IconName } from '@libs/shared/models';
import { ScrollService } from '@libs/shared/services';
import { ConditionalFieldsDirective } from '../../../../directives';
import { DYNAMIC_ELEMENTS_MAP, GroupBaseComponent } from '../../../../models';
import { FormConfigService } from '../../../../services';
import { SingleItemsMap } from '../../../single-items-map';
import { StepComponent } from '../../step';

/**
 * A handler for asynchronous submitting of the step
 *
 * Parameters:
 *  1. `currentStep` - current step item.
 *  2. `fullFormValue` - full form value including current step.
 *  3. `submitterName` - name property of the submit button/element
 *
 * Returns a promise of `StepItemModel` that defines next step or undefined.
 *
 * Throws `ValidationErrors`
 */
export type StepSubmitHandler = (currentStep: StepItemModel, fullFormValue: GroupValueType, submitterName?: string) => Promise<StepItemModel | undefined>;

@Component({
  selector: 'ui-mat-step-extended',
  template: `
    <ng-template>
      <ng-content></ng-content>
      <ng-template [cdkPortalOutlet]="_portal"></ng-template>
    </ng-template>
  `,
  providers: [
    { provide: ErrorStateMatcher, useExisting: MatStepExtended },
    { provide: MatStep, useExisting: MatStepExtended },
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkPortalOutlet,
  ],
  host: {
    hidden: '', // Hide the steps so they don't affect the layout.
  },
})
export class MatStepExtended extends MatStep {
  private readonly form: MultiStepFormComponent = inject(MultiStepFormComponent);

  public override select(): void {
    // Prevent moving forward if the current step needs submission
    if (this.index() > this._stepper.selectedIndex) {
      return;
    }

    super.select();
  }
}

@Component({
  selector: 'ui-multi-step-form',
  templateUrl: './multi-step-form.component.html',
  styleUrls: ['./multi-step-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatStepper,
    MatStepLabel,
    ButtonComponent,
    IconComponent,
    MatStepExtended,
    StepComponent,
    ConditionalFieldsDirective,
    MatStepperIcon,
  ],
  providers: [
    FormConfigService,
    {
      provide: DYNAMIC_ELEMENTS_MAP,
      useValue: {
        ...SingleItemsMap,
        step: StepComponent,
      },
    },
  ],
})
export class MultiStepFormComponent extends GroupBaseComponent implements OnChanges {
  private readonly formService: FormConfigService = inject(FormConfigService);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  // Reference to scroll anchor at the top of the step
  private readonly formTop: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly scrollService: ScrollService = inject(ScrollService);

  private readonly currentStepControl: Signal<FormGroup> = computed(() => {
    return this.getStepControl(this.currentStep());
  });

  protected readonly stepper: Signal<MatStepper> = viewChild.required(MatStepper);
  protected readonly currentStep: Signal<StepItemModel> = computed(() => {
    return this.config().items[this.stepper().selectedIndex];
  });

  @Input() public data!: MultiStepFormItemModel;
  @Input() public hideTitle: boolean = false;
  @Input() public submitHandler?: StepSubmitHandler;
  @Input() public override control: FormGroup = new FormGroup({});

  /**
   * Emits the step the user is leaving when they click a `prev`-named submitter.
   * Useful for consumers that need to react to backwards navigation
   * (e.g. analytics) without owning the stepper state itself.
   */
  public readonly stepBack: OutputEmitterRef<StepItemModel> = output<StepItemModel>();

  public readonly stepNumberIcons: IconName[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  public readonly config: WritableSignal<MultiStepFormItemModel> = signal({
    type: 'form',
    name: 'form',
    items: [],
    buttonLabels: {
      next: 'Weiter',
      prev: 'Zurück',
      done: '',
    },
  });

  public readonly isLoading: WritableSignal<boolean> = signal(false);

  public readonly isLinear: Signal<boolean> = computed(() => Boolean(this.config().isLinear));

  public ngOnChanges(): void {
    this.stepper().reset();
    this.stepper().selectedIndex = 0;

    // keep the possibly injected control instance; just rebuild its children
    Object.keys(this.control.controls).forEach(name => {
      this.control.removeControl(name)
    });
    this.data.items.forEach(step => {
      this.control.addControl(step.name, new FormGroup({}));
    });
    this.config.set(this.data);
    this.formService.setConfig({
      form: this.config(),
      control: this.control,
    });
  }

  public getStepControl(step: StepItemModel): FormGroup {
    return this.control.controls[step.name] as FormGroup;
  }

  public onSelectedIndexChange(): void {
    this.stepper().steps.forEach((matStep, index) => {
      matStep.completed = index < this.stepper().selectedIndex;
      matStep.interacted = matStep.completed;
    });
  }

  public onStepSubmit(event: SubmitEvent): void {
    const submitterName: string | undefined = event.submitter?.getAttribute('name') ?? undefined;

    if (submitterName === 'prev') {
      this.onPreviousClick();

      return;
    }

    this.onNextClick(submitterName);
  }

  public onPreviousClick(): void {
    this.stepBack.emit(this.currentStep());
    this.stepper().previous();
    this.scrollToFormTop();
  }

  public onNextClick(submitterName?: string): void {
    const step: StepItemModel = this.currentStep();
    const control: FormGroup = this.currentStepControl();

    control.markAllAsTouched();
    control.markAllAsDirty();

    control.updateValueAndValidity();
    this.scrollToFormTop();

    if (!control.valid) {
      return;
    }

    if (!this.submitHandler) {
      this.stepper().next();

      return;
    }

    this.isLoading.set(true);

    void this.submitHandler(step, this.control.value as GroupValueType, submitterName)
      .then(nextStep => {
        if (nextStep) {
          this.replaceNextStep(nextStep);
        }

        this.cdr.detectChanges();

        void this.stepper().next();
      })
      .catch((errors: ValidationErrors) => {
        this.handleValidationErrors(errors);
      })
      .finally(() => {
        this.isLoading.set(false);
      });
  }

  private scrollToFormTop(): void {
    this.scrollService.contentScrollToTop(this.formTop.nativeElement);
  }

  private replaceNextStep(nextStep: StepItemModel): void {
    const index: number = this.stepper().selectedIndex;
    const steps: StepItemModel[] = this.config().items;

    const oldStep: StepItemModel = steps[index + 1];

    this.control.removeControl(oldStep.name, { emitEvent: false });

    steps[index + 1] = nextStep;
    this.control.addControl(nextStep.name, new FormGroup({}), { emitEvent: false });

    this.config.set({
      ...this.config(),
      items: steps,
    });

    this.formService.setConfig({
      form: this.config(),
      control: this.control,
    });
  }

  private handleValidationErrors(errors: ValidationErrors): void {
    console.error(errors);
  }
}
